import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Badge,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AccountBalance as AccountBalanceIcon,
  Warning as WarningIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  AttachMoney as AttachMoneyIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  LocationOn as LocationOnIcon,
  Percent as PercentIcon,
  Edit as EditIcon,
  Description as DescriptionIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import DashboardHome from './DashboardHome';
import LoanManagement from './LoanManagement';
import UserManagement from './UserManagement';
import LatePaymentManagement from './LatePaymentManagement';
import LoanApproval from './LoanApproval';
import CustomerRequestApproval from './CustomerRequestApproval';
import CustomerLoanSchedule from './CustomerLoanSchedule';
import CapitalManagement from './CapitalManagement';
import ExpenseManagement from './ExpenseManagement';
import CapitalOverview from './CapitalOverview';
import CollectionsAudit from './CollectionsAudit';
import LoanArrears from './LoanArrears';
import AreaManagement from './AreaManagement';
import InterestConfiguration from './InterestConfiguration';
import PaymentEditRequests from './PaymentEditRequests';
import InterestChangeRequests from './InterestChangeRequests';
import MonthlyReport from './MonthlyReport';
import LoanDisbursementRecovery from './LoanDisbursementRecovery';
import UserActivityTracking from './UserActivityTracking';

const drawerWidth = 260;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('dashboard');
  const [notificationCounts, setNotificationCounts] = useState({
    pendingRequests: 0,
    pendingLoans: 0,
    overdueInstallments: 0,
    pendingPaymentEditRequests: 0,
    pendingInterestChangeRequests: 0,
  });

  useEffect(() => {
    fetchNotificationCounts();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchNotificationCounts();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Update selected menu based on current path
    const path = location.pathname.split('/').pop() || 'dashboard';
    setSelectedMenu(path);
  }, [location]);

  const fetchNotificationCounts = async () => {
    try {
      const [notificationsRes, paymentEditRequestsRes, interestChangeRequestsRes] = await Promise.allSettled([
        api.get('/admin/notifications/counts'),
        api.get('/admin/payment-edit-requests/pending'),
        api.get('/admin/interest-change-requests/pending'),
      ]);
      
      const notificationsData = notificationsRes.status === 'fulfilled' ? notificationsRes.value.data : {};
      const paymentEditRequests = paymentEditRequestsRes.status === 'fulfilled' ? paymentEditRequestsRes.value.data : [];
      const interestChangeRequests = interestChangeRequestsRes.status === 'fulfilled' ? interestChangeRequestsRes.value.data : [];
      
      setNotificationCounts({
        ...notificationsData,
        pendingPaymentEditRequests: paymentEditRequests.length || 0,
        pendingInterestChangeRequests: interestChangeRequests.length || 0,
      });
    } catch (error) {
      console.error('Error fetching notification counts:', error);
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: 'dashboard' },
    { text: 'Capital Overview', icon: <TrendingUpIcon />, path: 'capital-overview' },
    { text: 'Capital Management', icon: <AttachMoneyIcon />, path: 'capital' },
    { text: 'Expense Management', icon: <ReceiptIcon />, path: 'expenses' },
    { 
      text: 'Loan Approval', 
      icon: <CheckCircleIcon />, 
      path: 'loan-approval',
      badgeCount: notificationCounts.pendingLoans,
      badgeColor: 'primary'
    },
    { text: 'Add Loan Template', icon: <AccountBalanceIcon />, path: 'loan-management' },
    { text: 'Customer Loan Schedules', icon: <AccountBalanceIcon />, path: 'customer-loan-schedule' },
    { 
      text: "Officer's Requests", 
      icon: <AssignmentIcon />, 
      path: 'customer-requests',
      badgeCount: notificationCounts.pendingRequests,
      badgeColor: 'warning'
    },
    { text: 'User Management', icon: <PeopleIcon />, path: 'users' },
    { text: 'Area Management', icon: <LocationOnIcon />, path: 'areas' },
    { 
      text: 'Late Payments', 
      icon: <WarningIcon />, 
      path: 'late-payments',
      badgeCount: notificationCounts.overdueInstallments,
      badgeColor: 'error'
    },
    { text: 'Collections Audit', icon: <AssessmentIcon />, path: 'collections-audit' },
    { text: 'Loan Arrears', icon: <WarningIcon />, path: 'loan-arrears' },
    { text: 'Monthly Arrears Report', icon: <DescriptionIcon />, path: 'monthly-report' },
    { text: 'Disbursement & Recovery', icon: <TrendingUpIcon />, path: 'disbursement-recovery' },
    { text: 'Interest Configuration', icon: <PercentIcon />, path: 'interest-configuration' },
    { 
      text: 'Payment Edit Requests', 
      icon: <EditIcon />, 
      path: 'payment-edit-requests',
      badgeCount: notificationCounts.pendingPaymentEditRequests,
      badgeColor: 'warning'
    },
    { 
      text: 'Interest Change Requests', 
      icon: <PercentIcon />, 
      path: 'interest-change-requests',
      badgeCount: notificationCounts.pendingInterestChangeRequests,
      badgeColor: 'warning'
    },
    { 
      text: 'User Activity Tracking', 
      icon: <HistoryIcon />, 
      path: 'user-activity-tracking'
    },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (item) => {
    setSelectedMenu(item.path);
    navigate(`/admin/${item.path}`);
    if (isMobile) {
      setMobileOpen(false);
    }
    if (item.badgeCount !== undefined) {
      fetchNotificationCounts();
    }
  };

  const drawer = (
    <Box sx={{ bgcolor: '#FFFFFF', height: '100%' }}>
      <Toolbar
        sx={{
          background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
          color: 'white',
          minHeight: { xs: '56px', sm: '64px' },
        }}
      >
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
          Admin Panel
        </Typography>
      </Toolbar>
      <Divider />
      <Box sx={{ overflow: 'auto', mt: 1, bgcolor: '#FFFFFF' }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={selectedMenu === item.path}
                onClick={() => handleMenuClick(item)}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 2,
                  bgcolor: selectedMenu === item.path ? '#0D47A1' : 'transparent',
                  color: selectedMenu === item.path ? 'white' : '#0D47A1',
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                    '& .MuiListItemText-primary': {
                      fontWeight: 700,
                      color: 'white',
                    },
                  },
                  '&:hover': {
                    backgroundColor: selectedMenu === item.path ? 'rgba(13, 71, 161, 0.9)' : 'rgba(13, 71, 161, 0.08)',
                  },
                  }}
                >
                <ListItemIcon
                  sx={{
                    color: selectedMenu === item.path ? 'white' : '#0D47A1',
                    minWidth: 40,
                  }}
                >
                    {item.badgeCount !== undefined && item.badgeCount > 0 ? (
                      <Badge badgeContent={item.badgeCount} color={item.badgeColor || 'error'}>
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                    fontWeight: selectedMenu === item.path ? 700 : 500,
                    color: selectedMenu === item.path ? 'white' : '#0D47A1',
                  }}
                />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#FFFFFF' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
          boxShadow: '0 4px 20px rgba(13, 71, 161, 0.4)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Admin Dashboard
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              mr: 2, 
              display: { xs: 'none', sm: 'block' },
              fontWeight: 500,
            }}
          >
            {user?.fullName}
          </Typography>
          <Button 
            color="inherit" 
            onClick={handleLogout} 
            startIcon={<LogoutIcon />}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Logout
            </Box>
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: '#FFFFFF',
              borderRight: '2px solid #0D47A1',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: '#FFFFFF',
              borderRight: '2px solid #0D47A1',
            },
          }}
          open
        >
          {drawer}
      </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          maxWidth: '100%',
          overflowX: 'hidden',
          bgcolor: '#FFFFFF',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' } }} />
        <Routes>
          <Route path="" element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DashboardHome />} />
          <Route path="capital-overview" element={<CapitalOverview />} />
          <Route path="capital" element={<CapitalManagement />} />
          <Route path="expenses" element={<ExpenseManagement />} />
          <Route path="loan-approval" element={<LoanApproval />} />
          <Route path="loan-management" element={<LoanManagement />} />
          <Route path="customer-loan-schedule" element={<CustomerLoanSchedule />} />
          <Route path="customer-loan-schedule/:customerId" element={<CustomerLoanSchedule />} />
          <Route path="customer-loan-schedule/:customerId/loans/:loanId" element={<CustomerLoanSchedule />} />
          <Route path="customer-requests" element={<CustomerRequestApproval />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="late-payments" element={<LatePaymentManagement />} />
          <Route path="collections-audit" element={<CollectionsAudit />} />
          <Route path="loan-arrears" element={<LoanArrears />} />
          <Route path="monthly-report" element={<MonthlyReport />} />
          <Route path="disbursement-recovery" element={<LoanDisbursementRecovery />} />
          <Route path="areas" element={<AreaManagement />} />
          <Route path="interest-configuration" element={<InterestConfiguration />} />
          <Route path="payment-edit-requests" element={<PaymentEditRequests />} />
          <Route path="interest-change-requests" element={<InterestChangeRequests />} />
          <Route path="user-activity-tracking" element={<UserActivityTracking />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default AdminDashboard;

